import { ReactNode, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { useCheckMemberAssignment } from "modules/ViewPAMemberProfile/UseCase";
import Loader from "components/Loader";
import PermissionDenied from "components/PermissionDenied";

import { logout } from "helpers/logout";
import { useRenderStatus } from "hooks/useRenderStatus";
import { useAuthStore, useBusinessUnitStore, useLayoutStore } from "store";

import { RouteType } from "types/index";
import { corpSlugParam } from "routes/constants";

interface AuthRouteProps {
  route: RouteType;
  children: ReactNode;
}

const AuthRoute = ({ children, route }: AuthRouteProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const busy = useRenderStatus(500);
  const { loadingCheckMemberAssignment, onCheckMemberAssignment } =
    useCheckMemberAssignment();
  const selectedBu = useBusinessUnitStore((state) => state.selectedBU);
  const user = useAuthStore((state) => state.user);
  const { state } = useLocation();
  const { personalSlug, [corpSlugParam]: corpSlug } = useParams();
  const [isAssigned, setIsAssigned] = useState(false);
  const onSetAccessDenied = useLayoutStore((state) => state.onSetAccessDenied);

  useEffect(() => {
    const checkAssignment = async () => {
      let hasAssignment = false;
      if (user.idUnique === personalSlug) {
        hasAssignment = true;
      } else {
        hasAssignment = await onCheckMemberAssignment({
          params: {
            idManager: user.idUnique,
            idMember: personalSlug || corpSlug || "",
          },
          buId: selectedBu?.idUnique || "",
        });
      }

      setIsAssigned(hasAssignment);
      onSetAccessDenied(!hasAssignment);
    };

    if (route.requireAssignment && !state?.hasAssignment) {
      checkAssignment();
    } else {
      onSetAccessDenied(false);
      setIsAssigned(true);
    }
  }, [
    corpSlug,
    personalSlug,
    route.requireAssignment,
    selectedBu?.idUnique,
    state?.hasAssignment,
    user.idUnique,
    onSetAccessDenied,
    onCheckMemberAssignment,
  ]);

  useEffect(() => {
    if (route.requiredAuth) {
      if (!busy && !isLogin) {
        logout();
      }
    }
  }, [busy, isLogin, route.requiredAuth]);

  if (busy || loadingCheckMemberAssignment) {
    return <Loader />;
  }

  if ((busy || !isLogin) && route.requiredAuth) {
    return <Loader />;
  }

  if (!isAssigned && route.requireAssignment) {
    return <PermissionDenied />;
  }

  return <>{children}</>;
};

export default AuthRoute;
